import React, { useEffect, useRef, useState } from "react";
import {
    Breadcrumbs,
    Button,
    Card,
    FormControl,
    FormLabel,
    Link,
    Option,
    Select,
    Table,
    Typography,
    Input
} from "@mui/joy";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import GeneralApiService, { GetAllFranchises, GetUser } from "../../axios/Axios";
import { toast, ToastContainer } from "react-toastify";
import ResponsiveTableWrapper from "../ui/ResponsiveTableWrapper";

interface ExpenseRow {
    id: number;
    franchise: number;
    franchise_location: string;
    name: string;
    expense_type: "monthly" | "yearly";
    price: string | number;
}

interface FranchiseOption {
    id: number;
    location: string;
}

interface UserResponse {
    location_permissions?: string[];
}

const getSlug = (value: string) => value.toLowerCase().replaceAll(" ", "_");
const AVERAGE_MONTHS = 3;

export default function Expenses() {
    const location = localStorage.getItem("location");
    const isAllContext = location === "All";
    const [franchises, setFranchises] = useState<FranchiseOption[]>([]);
    const [selectedFranchiseId, setSelectedFranchiseId] = useState<number | null>(null);
    const [rows, setRows] = useState<ExpenseRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalMonthly, setTotalMonthly] = useState(0);
    const [marketingSpendMonthlyAvg, setMarketingSpendMonthlyAvg] = useState(0);
    const [salesSpendMonthlyAvg, setSalesSpendMonthlyAvg] = useState(0);
    const rowsRef = useRef<ExpenseRow[]>([]);
    const autoSaveTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

    useEffect(() => {
        const initialize = async () => {
            setLoading(true);
            try {
                if (isAllContext) {
                    const [franchiseResponse, userResponse] = await Promise.all([GetAllFranchises(), GetUser()]);
                    const allFranchises: FranchiseOption[] = franchiseResponse.data || [];
                    const userData: UserResponse = userResponse.data || {};
                    const perms = userData.location_permissions || [];
                    const hasAll = perms.includes("franchise.location.all");

                    const allowedFranchises = hasAll
                        ? allFranchises
                        : allFranchises.filter((franchise) =>
                            perms.includes(`franchise.location.${getSlug(franchise.location)}`)
                        );

                    setFranchises(allowedFranchises);
                    if (allowedFranchises.length === 1) {
                        setSelectedFranchiseId(allowedFranchises[0].id);
                    }
                    setRows([]);
                    setTotalMonthly(0);
                } else {
                    await loadExpenses(null);
                }
            } catch (error) {
                toast.error("Could not load expenses.");
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, [isAllContext]);

    useEffect(() => {
        if (!isAllContext) {
            return;
        }

        if (!selectedFranchiseId) {
            setRows([]);
            setTotalMonthly(0);
            return;
        }

        loadExpenses(selectedFranchiseId);
    }, [selectedFranchiseId, isAllContext]);

    const loadExpenses = async (franchiseId: number | null) => {
        setLoading(true);
        try {
            const data = await GeneralApiService.getFranchiseExpenses(franchiseId);
            setRows(data.expenses || []);
            setTotalMonthly(Number(data.total_monthly || 0));
        } catch (error) {
            toast.error("Could not load expenses.");
            setRows([]);
            setTotalMonthly(0);
        } finally {
            setLoading(false);
        }
    };

    const getSummaryLocationSlug = () => {
        if (isAllContext) {
            const selectedFranchise = franchises.find((franchise) => franchise.id === selectedFranchiseId);
            return selectedFranchise ? getSlug(selectedFranchise.location) : null;
        }
        return location ? getSlug(location) : null;
    };

    const formatDateDDMMYYYY = (date: Date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const getSum = (items: any[], service: string, type: string) =>
        items
            .filter((item) => item.service === service && item.type === type)
            .reduce((sum, item) => sum + Number(item.total_amount || 0), 0);

    const loadAutoSpendRows = async () => {
        const summaryLocationSlug = getSummaryLocationSlug();
        if (!summaryLocationSlug) {
            setMarketingSpendMonthlyAvg(0);
            setSalesSpendMonthlyAvg(0);
            return;
        }

        const endDate = new Date();
        const startDate = new Date(endDate);
        startDate.setMonth(startDate.getMonth() - AVERAGE_MONTHS);

        try {
            const summary = await GeneralApiService.getAdBalanceChangesSummaryForLocation(
                summaryLocationSlug,
                formatDateDDMMYYYY(startDate),
                formatDateDDMMYYYY(endDate)
            );

            const byTypeAndService = summary?.by_type_and_service || [];

            const marketingTotal = getSum(byTypeAndService, "Google", "Charge") + getSum(byTypeAndService, "Facebook", "Charge");
            const leadConversionTotal = getSum(byTypeAndService, "Lead Conversion", "Charge");
            const leadConversionRefundTotal = getSum(byTypeAndService, "Lead Conversion Refund", "Charge");
            const salesNetTotal = leadConversionTotal - leadConversionRefundTotal;

            setMarketingSpendMonthlyAvg(marketingTotal / AVERAGE_MONTHS);
            setSalesSpendMonthlyAvg(salesNetTotal / AVERAGE_MONTHS);
        } catch (error) {
            setMarketingSpendMonthlyAvg(0);
            setSalesSpendMonthlyAvg(0);
        }
    };

    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    useEffect(() => {
        return () => {
            autoSaveTimersRef.current.forEach((timer) => clearTimeout(timer));
            autoSaveTimersRef.current.clear();
        };
    }, []);

    useEffect(() => {
        const monthlyTotal = rows.reduce((total, row) => {
            const parsedPrice = Number(row.price);
            const safePrice = Number.isFinite(parsedPrice) ? parsedPrice : 0;
            if (row.expense_type === "yearly") {
                return total + (safePrice / 12);
            }
            return total + safePrice;
        }, 0);
        setTotalMonthly(monthlyTotal + marketingSpendMonthlyAvg + salesSpendMonthlyAvg);
    }, [rows, marketingSpendMonthlyAvg, salesSpendMonthlyAvg]);

    useEffect(() => {
        if (isAllContext && !selectedFranchiseId) {
            setMarketingSpendMonthlyAvg(0);
            setSalesSpendMonthlyAvg(0);
            return;
        }
        loadAutoSpendRows();
    }, [isAllContext, selectedFranchiseId, franchises, location]);

    const normalizePriceForApi = (price: string | number) => {
        const asString = String(price ?? "").trim();
        const parsed = Number(asString);
        if (Number.isFinite(parsed)) {
            return parsed.toFixed(2);
        }
        return "0.00";
    };

    const buildPayload = (row: ExpenseRow) => ({
        name: row.name ?? "",
        expense_type: row.expense_type || "monthly",
        price: normalizePriceForApi(row.price),
    });

    const persistRowById = async (rowId: number) => {
        const row = rowsRef.current.find((item) => item.id === rowId);
        if (!row) {
            return;
        }
        try {
            await GeneralApiService.updateFranchiseExpense(row.id, buildPayload(row));
        } catch (error) {
            toast.error("Could not auto-save expense.");
        }
    };

    const scheduleAutoSave = (rowId: number) => {
        const existingTimer = autoSaveTimersRef.current.get(rowId);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }
        const nextTimer = setTimeout(() => {
            persistRowById(rowId);
            autoSaveTimersRef.current.delete(rowId);
        }, 500);
        autoSaveTimersRef.current.set(rowId, nextTimer);
    };

    const onAddRow = async () => {
        if (isAllContext && !selectedFranchiseId) {
            toast.info("Select a franchise first.");
            return;
        }

        try {
            await GeneralApiService.createFranchiseExpense(
                { name: "", expense_type: "monthly", price: "0.00" },
                isAllContext ? selectedFranchiseId : null
            );
            await loadExpenses(isAllContext ? selectedFranchiseId : null);
        } catch (error) {
            toast.error("Could not add expense row.");
        }
    };

    const onChangeRow = (id: number, key: keyof ExpenseRow, value: string) => {
        setRows((prev) => prev.map((row) => (
            row.id === id ? { ...row, [key]: value } : row
        )));
        scheduleAutoSave(id);
    };

    const onDeleteRow = async (row: ExpenseRow) => {
        const existingTimer = autoSaveTimersRef.current.get(row.id);
        if (existingTimer) {
            clearTimeout(existingTimer);
            autoSaveTimersRef.current.delete(row.id);
        }

        try {
            await GeneralApiService.deleteFranchiseExpense(row.id);
            toast.success("Expense deleted.");
            await loadExpenses(isAllContext ? selectedFranchiseId : null);
        } catch (error) {
            toast.error("Could not delete expense.");
        }
    };

    return (
        <>
            <ToastContainer />
            <Breadcrumbs size="sm" aria-label="breadcrumbs" separator={<ChevronRightRoundedIcon />} sx={{ pl: 0, mb: 1 }}>
                <Link underline="none" color="neutral" href="/dashboard" aria-label="Home"><HomeRoundedIcon /></Link>
                <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>Dashboard</Link>
                <Typography color="primary" fontWeight={500} fontSize={12}>Expenses</Typography>
            </Breadcrumbs>

            <Card>
                <Typography level="h3" sx={{ mb: 1 }}>Expenses</Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                    Track franchise costs. Yearly expenses are converted to monthly equivalent in the total.
                </Typography>

                {isAllContext && (
                    <FormControl sx={{ maxWidth: 360, mb: 2 }}>
                        <FormLabel>Franchise</FormLabel>
                        <Select
                            value={selectedFranchiseId}
                            onChange={(_, value) => setSelectedFranchiseId(value as number)}
                            placeholder="Select a franchise"
                        >
                            {franchises.map((franchise) => (
                                <Option key={franchise.id} value={franchise.id}>
                                    {franchise.location}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <Typography level="title-lg" sx={{ mb: 2 }}>
                    Total Expenses (Monthly): GBP {totalMonthly.toFixed(2)}
                </Typography>

                <ResponsiveTableWrapper minWidth={760}>
                    <Table hoverRow stickyHeader>
                        <thead>
                            <tr>
                                <th style={{ width: "40%" }}>Expense Name</th>
                                <th style={{ width: "20%" }}>Type</th>
                                <th style={{ width: "20%" }}>Price (GBP)</th>
                                <th style={{ width: "20%" }}>Delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!isAllContext || selectedFranchiseId ? (
                                <>
                                    <tr>
                                        <td>
                                            <Input value="Marketing Spend" disabled sx={{ backgroundColor: "#fff" }} />
                                        </td>
                                        <td>
                                            <Select value="monthly" disabled sx={{ backgroundColor: "#fff" }}>
                                                <Option value="monthly">Monthly</Option>
                                            </Select>
                                        </td>
                                        <td>
                                            <Input value={marketingSpendMonthlyAvg.toFixed(2)} disabled sx={{ backgroundColor: "#fff" }} />
                                        </td>
                                        <td>
                                            <Typography level="body-sm" color="neutral">Auto</Typography>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <Input value="Sales Spend" disabled sx={{ backgroundColor: "#fff" }} />
                                        </td>
                                        <td>
                                            <Select value="monthly" disabled sx={{ backgroundColor: "#fff" }}>
                                                <Option value="monthly">Monthly</Option>
                                            </Select>
                                        </td>
                                        <td>
                                            <Input value={salesSpendMonthlyAvg.toFixed(2)} disabled sx={{ backgroundColor: "#fff" }} />
                                        </td>
                                        <td>
                                            <Typography level="body-sm" color="neutral">Auto</Typography>
                                        </td>
                                    </tr>
                                    {rows.map((row) => (
                                        <tr key={row.id}>
                                            <td>
                                                <Input
                                                    value={row.name}
                                                    onChange={(event) => onChangeRow(row.id, "name", event.target.value)}
                                                    placeholder="Expense name"
                                                    sx={{ backgroundColor: "#fff" }}
                                                />
                                            </td>
                                            <td>
                                                <Select
                                                    value={row.expense_type}
                                                    onChange={(_, value) => onChangeRow(row.id, "expense_type", (value as string) || "monthly")}
                                                    sx={{ backgroundColor: "#fff" }}
                                                >
                                                    <Option value="monthly">Monthly</Option>
                                                    <Option value="yearly">Yearly</Option>
                                                </Select>
                                            </td>
                                            <td>
                                                <Input
                                                    type="number"
                                                    slotProps={{ input: { step: "0.01", min: "0" } }}
                                                    value={row.price}
                                                    onChange={(event) => onChangeRow(row.id, "price", event.target.value)}
                                                    sx={{ backgroundColor: "#fff" }}
                                                />
                                            </td>
                                            <td>
                                                <Button
                                                    size="sm"
                                                    color="danger"
                                                    variant="outlined"
                                                    startDecorator={<DeleteOutlineRoundedIcon />}
                                                    onClick={() => onDeleteRow(row)}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td colSpan={4} style={{ padding: "10px" }}>
                                            <Button
                                                variant="plain"
                                                startDecorator={<AddRoundedIcon />}
                                                onClick={onAddRow}
                                                disabled={loading}
                                                sx={{
                                                    width: "100%",
                                                    border: "2px dashed",
                                                    borderColor: "divider",
                                                    borderRadius: "10px",
                                                    py: 1.25,
                                                    justifyContent: "center",
                                                    backgroundColor: "transparent",
                                                    "&:hover": {
                                                        backgroundColor: "rgba(255, 255, 255, 0.28)",
                                                        borderColor: "neutral.outlinedBorder",
                                                    },
                                                }}
                                            >
                                                {loading ? "Loading expenses..." : "Add new expenses row"}
                                            </Button>
                                        </td>
                                    </tr>
                                </>
                            ) : (
                                <tr>
                                    <td colSpan={4}>
                                        <Typography level="body-sm" sx={{ p: 2, textAlign: "center" }}>
                                            Select a franchise to manage expenses.
                                        </Typography>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </ResponsiveTableWrapper>
            </Card>
        </>
    );
}
